import React, { useState } from 'react';
import { X, Search, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type Promoter = {
  id: string;
  firstName: string;
  lastName: string;
  name?: string;
  email: string;
  company?: string;
};

type TeamMember = {
  id: string;
  name: string;
  role: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (selectedPromoterIds: string[]) => void;
  teamMember: TeamMember;
  allPromoters: Promoter[];
  assignedPromoterIds: string[];
};

const AssignPromotersModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onAssign,
  teamMember,
  allPromoters,
  assignedPromoterIds,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'teams' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPromoters, setSelectedPromoters] = useState<string[]>([]);

  if (!isOpen) return null;

  const availablePromoters = allPromoters.filter((p) => !assignedPromoterIds.includes(p.id));

  const filteredPromoters = availablePromoters.filter(
    (promoter) => {
      const name = promoter.name ?? `${promoter.firstName ?? ''} ${promoter.lastName ?? ''}`.trim();
      return (
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        promoter.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (promoter.company ?? '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  );

  const togglePromoter = (promoterId: string) => {
    setSelectedPromoters((prev) =>
      prev.includes(promoterId) ? prev.filter((id) => id !== promoterId) : [...prev, promoterId]
    );
  };

  const handleAssign = () => {
    if (selectedPromoters.length > 0) {
      onAssign(selectedPromoters);
      setSelectedPromoters([]);
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    setSelectedPromoters([]);
    setSearchTerm('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('assignPromoters.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('assignPromoters.subtitle', { name: teamMember.name })}</p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('assignPromoters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {selectedPromoters.length > 0 && (
            <div className="mt-3 text-sm text-gray-600">
              {t('assignPromoters.selected', { count: selectedPromoters.length })}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {filteredPromoters.length > 0 ? (
            <div className="space-y-2">
              {filteredPromoters.map((promoter) => {
                const isSelected = selectedPromoters.includes(promoter.id);
                const name = promoter.name ?? `${promoter.firstName ?? ''} ${promoter.lastName ?? ''}`.trim();
                return (
                  <div
                    key={promoter.id}
                    onClick={() => togglePromoter(promoter.id)}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{name || promoter.email}</h4>
                        <p className="text-sm text-gray-500 mt-1">{promoter.email}</p>
                        {promoter.company && (
                          <p className="text-sm text-gray-600 mt-0.5">{promoter.company}</p>
                        )}
                      </div>
                      {isSelected && <CheckCircle2 className="w-6 h-6 text-blue-500 flex-shrink-0" />}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm
                  ? t('assignPromoters.noSearchResults')
                  : t('assignPromoters.noAvailable')}
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={handleAssign}
            disabled={selectedPromoters.length === 0}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPromoters.length > 0
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {selectedPromoters.length > 0
              ? t('assignPromoters.assignWithCount', { count: selectedPromoters.length })
              : t('assignPromoters.assign')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignPromotersModal;
